import { useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import { ShoppingBag as ShoppingBagIcon } from "lucide-react";
import { subscriptionsService, isShopifySubscription } from "../../lib/api/subscriptions";
import { buildShopifyManageBillingUrl } from "../../lib/shopifyAdminUrl";
import { type Plan as Product } from "../../lib/api/plans";
import { useAlertStore } from "../../stores/alertStore";
import { PlanSwitchModal } from "./PlanSwitchModal";
import { PlansGrid } from "../../components/pricing/PlansGrid";

const DowngradeConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
}: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
}) => {
    const { t } = useTranslation("common");
    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Dialog.Panel className="w-full max-w-sm transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-6 shadow-xl transition-all">
                            <Dialog.Title className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">
                                {t("plans.confirmDowngrade", "Switch to Free Plan?")}
                            </Dialog.Title>
                            <div className="mt-2">
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {t("plans.downgradeMessage", "Your current subscription will be canceled at the end of the billing period, and you will revert to the free plan.")}
                                </p>
                            </div>
                            <div className="mt-4 flex gap-3 justify-end">
                                <button
                                    className="inline-flex justify-center rounded-md border border-transparent bg-neutral-100 dark:bg-neutral-800 px-4 py-2 text-sm font-medium text-neutral-900 dark:text-neutral-100 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
                                    onClick={onClose}
                                >
                                    {t("actions.cancel")}
                                </button>
                                <button
                                    className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors"
                                    onClick={onConfirm}
                                >
                                    {t("actions.confirm")}
                                </button>
                            </div>
                        </Dialog.Panel>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

const PlanPage = () => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const { showAlert } = useAlertStore();
    const queryClient = useQueryClient();

    const [showSwitchModal, setShowSwitchModal] = useState(false);
    const [showDowngradeModal, setShowDowngradeModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<Product | null>(null);
    const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);

    const { data: subscriptionsResponse } = useQuery({
        queryKey: ["subscriptions"],
        queryFn: subscriptionsService.getMySubscriptions,
        retry: false,
        staleTime: 5 * 60 * 1000,
    });

    // Only consider Stripe subscriptions here — a lapsed Shopify sub is
    // past_due and would otherwise drive a false "payment issue" warning.
    const subscription = subscriptionsResponse?.data?.find(
        (sub) =>
            !isShopifySubscription(sub) &&
            (sub.status === "active" || sub.status === "trialing" || sub.status === "past_due" || sub.status === "incomplete"),
    ) || null;

    const handleSelectPlan = async (plan: Product, priceId?: string) => {
        if (!subscription) {
            if (!priceId) return;
            try {
                const { url } = await subscriptionsService.createCheckoutSession(priceId, window.location.href);
                if (url) window.location.href = url;
            } catch (error) {
                console.error("Checkout failed", error);
                const apiError = (error as { response?: { data?: { code?: string; error?: string } } })?.response?.data;
                if (apiError?.code === "CURRENCY_LOCKED") {
                    showAlert({
                        message: apiError.error || String(t("userErrors.subscription.currencyLocked")),
                        type: "error",
                    });
                } else {
                    showAlert({
                        message: String(t("userErrors.subscription.unableToCheckout", "Unable to proceed to checkout.")),
                        type: "error",
                    });
                }
            }
        } else {
            if (!priceId || plan.name === "Free") {
                setShowDowngradeModal(true);
            } else {
                setSelectedPlan(plan);
                setSelectedPriceId(priceId);
                setShowSwitchModal(true);
            }
        }
    };

    const handleDowngradeToFree = async () => {
        try {
            await subscriptionsService.downgradeToFree();
            setShowDowngradeModal(false);
            queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
        } catch (error) {
            showAlert({ message: String(t("plans.downgradeFailed")), type: "error" });
        }
    };

    const handleSwitchSuccess = () => {
        queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
    };

    // When this account is billed via Shopify, Stripe-side plan management is
    // unavailable. Surface a read-only view directing the merchant to manage
    // billing in Shopify admin (compliance: section 1.2.1 + EC-18).
    const { data: billingStatusResponse } = useQuery({
        queryKey: ["subscriptions", "billing-status"],
        queryFn: subscriptionsService.getBillingStatus,
        retry: false,
        staleTime: 60 * 1000,
    });
    const billingStatus = billingStatusResponse?.data;

    // For Shopify-billed accounts, plan management always happens in Shopify
    // admin — never via dashbal.com. This is required for Shopify App Store
    // compliance: we must not surface any off-platform billing pathway to
    // merchants who pay through Shopify. The lock applies in grace and suspended
    // states too: the only recovery option is reinstall on Shopify.
    // Lock to the read-only "managed by Shopify" view ONLY while a Shopify
    // subscription is actually ACTIVE. Once it has lapsed (grace/read_only/
    // suspended, e.g. after an uninstall) the account is "open" — fall through
    // to the normal plans grid so the merchant can subscribe via the website.
    if (
        billingStatus?.billingSource === "shopify" &&
        billingStatus?.billingStatus === "active"
    ) {
        const shopAdminUrl = buildShopifyManageBillingUrl(billingStatus.shopifyBillingShop);
        return (
            <div className="bg-background-page flex flex-col font-sans text-text-primary min-h-screen">
                <div className="h-16 flex items-center px-6 pt-4 pb-0">
                    <button
                        onClick={() => navigate("/boards")}
                        className="p-2 transition-colors text-neutral-400 hover:text-black dark:hover:text-white"
                        aria-label={String(t("actions.back"))}
                    >
                        ←
                    </button>
                </div>
                <div className="container-custom flex-1 flex items-center justify-center">
                    <div className="card-elevated max-w-md w-full p-8 text-center space-y-4">
                        <div className="mx-auto w-12 h-12 rounded-full bg-shopify/10 flex items-center justify-center">
                            <ShoppingBagIcon className="w-6 h-6 text-shopify" />
                        </div>
                        <h2 className="text-h2 font-display font-semibold">
                            {t("billing.managedByShopify.title", "Billed via Shopify")}
                        </h2>
                        <p className="text-small text-neutral-600 dark:text-neutral-400">
                            {t("billing.managedByShopify.body", "Your subscription is managed in your Shopify admin. Update payment methods, change plans, or cancel from there.")}
                        </p>
                        {shopAdminUrl && (
                            <a
                                href={shopAdminUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-primary inline-block px-6 py-2.5"
                            >
                                {t("billing.managedByShopify.openShopify", "Manage in Shopify")}
                            </a>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-background-page flex flex-col font-sans text-text-primary min-h-screen">
            <div className="h-16 flex items-center px-6 pt-4 pb-0">
                <button
                    onClick={() => navigate("/boards")}
                    className="p-2 transition-colors text-neutral-400 hover:text-black dark:hover:text-white"
                    aria-label={String(t("actions.back"))}
                >
                    <span
                        className="w-6 h-6"
                        style={{
                            maskImage: `url(https://cdn.dashbal.com/assets/icon_b547bb65.svg)`,
                            WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_b547bb65.svg)`,
                            maskRepeat: "no-repeat",
                            WebkitMaskRepeat: "no-repeat",
                            maskPosition: "center",
                            WebkitMaskPosition: "center",
                            maskSize: "contain",
                            WebkitMaskSize: "contain",
                            backgroundColor: "currentColor",
                            display: "inline-block",
                        }}
                        role="img"
                    />
                </button>
            </div>

            <main className="flex-1 w-full max-w-7xl mx-auto px-6 flex flex-col">
                <div className="text-center pt-2 pb-10">
                    <h1 className="text-2xl font-medium text-black dark:text-white">{t("plans.unlockTitle")}</h1>
                    {(subscription?.status === "past_due" || subscription?.status === "incomplete") && (
                        <p className="mt-2 text-sm text-error-600 dark:text-error-400">
                            {t("plans.paymentIssue", "There is an issue with your payment method. Please update it in your profile to keep your subscription active.")}
                        </p>
                    )}
                </div>

                <PlansGrid
                    subscription={subscription}
                    onSelectPlan={handleSelectPlan}
                />
            </main>

            {subscription && subscription.variant && subscription.price && selectedPlan && selectedPriceId && (
                <PlanSwitchModal
                    isOpen={showSwitchModal}
                    onClose={() => setShowSwitchModal(false)}
                    targetPrice={selectedPlan.prices?.find((p) => p.id === selectedPriceId)!}
                    onSuccess={handleSwitchSuccess}
                />
            )}

            <DowngradeConfirmModal
                isOpen={showDowngradeModal}
                onClose={() => setShowDowngradeModal(false)}
                onConfirm={handleDowngradeToFree}
            />
        </div>
    );
};

export default PlanPage;
